'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

// Username → email mapping for convenience login
const USERNAME_MAP: Record<string, string> = {
  franco: 'franco@ercomex.com',
  ezequiel: 'ezequiel@ercomex.com',
  antonella: 'antonella@ercomex.com',
  mateo: 'mateo@ercomex.com',
  contador: 'contador@ercomex.com',
  empleado1: 'empleado1@ercomex.com',
  empleado2: 'empleado2@ercomex.com',
  empleado3: 'empleado3@ercomex.com',
};

function resolveEmail(input: string): string {
  const trimmed = input.trim().toLowerCase();
  if (USERNAME_MAP[trimmed]) return USERNAME_MAP[trimmed];
  if (trimmed.includes('@')) return trimmed;
  return trimmed + '@ercomex.com';
}

export default function LoginPage() {
  const router = useRouter();
  const supabase = createClient();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        router.replace('/');
      } else {
        setCheckingSession(false);
      }
    });
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      setError('Ingresá tu usuario y contraseña.');
      return;
    }
    setLoading(true);
    setError('');
    const email = resolveEmail(identifier);
    try {
      const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) {
        setError('Usuario o contraseña incorrectos.');
        // Log failed attempt to audit
        try {
          await supabase.from('user_audit_log').insert({
            accion: 'login-fallido',
            modulo: 'auth',
            detalle: `Intento de login fallido para: ${identifier}`,
          });
        } catch { /* silent */ }
      } else {
        // Log successful login
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            await supabase.from('user_audit_log').insert({
              usuario_id: user.id,
              usuario_nombre: user.email ?? identifier,
              accion: 'login-exitoso',
              modulo: 'auth',
              detalle: `Login exitoso para ${user.email}`,
            });
            // Update last_login in user_profiles
            await supabase.from('user_profiles').update({ last_login: new Date().toISOString() }).eq('id', user.id);
          }
        } catch { /* silent */ }
        // Small delay to ensure session cookies are set before navigation
        await new Promise(resolve => setTimeout(resolve, 100));
        router.push('/');
      }
    } catch {
      setError('Error de conexión. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center gap-3 mb-3">
            <AppLogo size={40} />
            <span className="text-2xl font-bold text-foreground tracking-tight">ERComex</span>
          </div>
          <p className="text-sm text-muted-foreground text-center">
            Sistema de Comercio Exterior
          </p>
        </div>

        {/* Card */}
        <div className="bg-secondary border border-border rounded-2xl p-8 shadow-xl">
          <h1 className="text-lg font-bold text-foreground mb-1">Iniciar sesión</h1>
          <p className="text-xs text-muted-foreground mb-6">Ingresá con tu usuario o email</p>

          <form onSubmit={handleLogin} className="space-y-4">
            {/* Identifier */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                Usuario o email
              </label>
              <div className="relative">
                <Icon name="UserIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={identifier}
                  onChange={e => { setIdentifier(e.target.value); setError(''); }}
                  placeholder="franco / franco@ercomex.com"
                  autoComplete="username"
                  autoFocus
                  className="w-full bg-background border border-border rounded-lg pl-9 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                Contraseña
              </label>
              <div className="relative">
                <Icon name="LockClosedIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full bg-background border border-border rounded-lg pl-9 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">
                <Icon name="ExclamationCircleIcon" size={16} className="text-red-400 shrink-0" />
                <p className="text-xs text-red-400">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-2.5 rounded-lg text-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Ingresando...
                </>
              ) : (
                <>
                  <Icon name="ArrowRightOnRectangleIcon" size={16} />
                  Ingresar
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          ER Comercio Internacional SRL — Sistema interno
        </p>
      </div>
    </div>
  );
}
